/**
 * Alpine.js data context for the natural-language video search.
 *
 * Usage in Fluid template:
 *   <div x-data="ymhNaturalSearch()"
 *        data-fetch-url="/yoga-videos?type=133&..."
 *        data-items-per-page="12">
 *     ...
 *   </div>
 *
 * Config is read from data-attributes inside init() via this.$root,
 * avoiding Fluid/Alpine quoting conflicts in x-data expressions.
 */

// ---------------------------------------------------------------------------
// Global NL search state + load-more button patching
// ---------------------------------------------------------------------------

interface NlSearchState {
  fetchUrl: string;
  query: string;
}

/** Shape of the persisted search snapshot stored in sessionStorage */
interface NlSearchSnapshot {
  query: string;
  itemsHtml: string;       // innerHTML of .tx-va-yoga-video-list-items .items
  nlFiltersJson: string;   // data-nl-filters JSON for filter-bar chip restore
  totalCount: number;
  fetchUrl: string;
  /** Page URL the search was performed on (guard against cross-page restore) */
  pageUrl: string;
}

const SESSION_KEY = "ymh_nl_search_snapshot";

declare global {
  interface Window {
    __ymhNlSearch?: NlSearchState;
    varioous?: {
      ajax_cl?: { initLoadMoreItemsButtons?: () => void };
      image_cl?: { preloadImages?: () => void };
    };
  }
}

/**
 * Patches all load-more buttons inside the video list to use the NL search
 * endpoint instead of the default filter action.
 *
 * If the hidden form was already built by the filter-bar's _patchNlLoadMoreWithFilters()
 * (which includes all manual filters), we only update the button's data attributes
 * without overwriting the richer form content.
 */
function patchNlLoadMoreButtons(): void {
  const state = window.__ymhNlSearch;
  if (!state) return;

  // Ensure hidden form exists for ajax.js to serialize
  let hiddenForm = document.querySelector(".nl-search-hidden-form") as HTMLFormElement | null;
  const formAlreadyHasFilters = hiddenForm && hiddenForm.querySelectorAll("input").length > 4;

  if (!hiddenForm) {
    hiddenForm = document.createElement("form");
    hiddenForm.className = "nl-search-hidden-form";
    hiddenForm.style.display = "none";
    document.body.appendChild(hiddenForm);
  }

  // Only write the minimal form if the filter-bar hasn't already built a richer one
  if (!formAlreadyHasFilters) {
    hiddenForm.innerHTML = [
      '<input type="hidden" name="tx_vayoga_videoall[action]" value="nlSearch">',
      '<input type="hidden" name="tx_vayoga_videoall[controller]" value="Video">',
      `<input type="hidden" name="tx_vayoga_videoall[query]" value="${state.query.replace(/"/g, "&quot;")}">`,
      '<input type="hidden" name="tx_vayoga_videoall[searchmode]" value="ALL">',
    ].join("");
  }

  // Patch every load-more button in the video list
  document
    .querySelectorAll<HTMLElement>(".tx-va-yoga-video-list-items .load-more-items")
    .forEach((el) => {
      el.dataset.url = state.fetchUrl;
      el.dataset.form = "nl-search-hidden-form";
    });
}

/** Starts a MutationObserver on the video list so newly appended load-more
 *  buttons (from ajax.js) are patched automatically. Idempotent. */
let nlObserverActive = false;
function ensureNlMutationObserver(): void {
  if (nlObserverActive) return;
  const container = document.querySelector(".tx-va-yoga-video-list-items");
  if (!container) return;
  new MutationObserver(() => {
    if (window.__ymhNlSearch) {
      setTimeout(patchNlLoadMoreButtons, 20);
    }
  }).observe(container, { childList: true, subtree: true });
  nlObserverActive = true;
}

// ---------------------------------------------------------------------------
// Typewriter placeholder phrases
// ---------------------------------------------------------------------------

const NL_SEARCH_PLACEHOLDERS = [
  "z.B. etwas Ruhiges ca. 20 Min …",
  "z.B. kraftvolles Yoga von Nancy …",
  "z.B. Yin Yoga für Anfänger …",
  "z.B. Meditation so 10 Minuten …",
  "z.B. intensives Workout, männlicher Lehrer …",
  "z.B. sanftes Abend-Yoga ohne Musik …",
  "z.B. Rücken-Yoga ca. 30 Min …",
];

// ---------------------------------------------------------------------------
// Alpine data context
// ---------------------------------------------------------------------------

export function naturalSearchDataContext() {
  return {
    query: "",
    isLoading: false,
    isSubmitted: false,
    hasError: false,
    hasResult: false,
    fetchUrl: "",
    itemsPerPage: 12,

    // Typewriter state
    _twPlaceholder: "",
    _twTimer: null as ReturnType<typeof setTimeout> | null,

    // Guards reset() against its own ymh:nl-reset dispatch re-entering via
    // this component's own @ymh:nl-reset.document listener (see reset()).
    _resettingSelf: false,

    init(this: any) {
      const root = this.$root as HTMLElement;
      this.fetchUrl = root?.dataset?.fetchUrl ?? "";
      this.itemsPerPage = Number(root?.dataset?.itemsPerPage ?? 12);

      // ── Back-navigation restore ────────────────────────────────────────────
      // When the user navigates to a video detail page and then presses Back,
      // the browser loads this page from bfcache or re-fetches it. We detect
      // this via the Navigation Timing API and restore the last search result
      // directly from sessionStorage — no new API call needed.
      if (this._restoreFromSession()) {
        // State restored: skip typewriter animation (query is already set)
        ensureNlMutationObserver();
        return;
      }

      this._twStartCycle();
      ensureNlMutationObserver();
    },

    // ---- Typewriter --------------------------------------------------------

    _twStartCycle(this: any) {
      let idx = 0;
      const self = this;

      const runPhrase = (phrase: string) => {
        let pos = 0;
        self._twPlaceholder = "";

        const typeNext = () => {
          if (self.query !== "") return;
          if (pos < phrase.length) {
            self._twPlaceholder = phrase.slice(0, ++pos);
            self._twTimer = setTimeout(typeNext, 45);
          } else {
            self._twTimer = setTimeout(eraseAll, 1800);
          }
        };

        const eraseAll = () => {
          if (self.query !== "") return;
          if (self._twPlaceholder.length > 0) {
            self._twPlaceholder = self._twPlaceholder.slice(0, -1);
            self._twTimer = setTimeout(eraseAll, 22);
          } else {
            idx = (idx + 1) % NL_SEARCH_PLACEHOLDERS.length;
            self._twTimer = setTimeout(() => runPhrase(NL_SEARCH_PLACEHOLDERS[idx]), 400);
          }
        };

        typeNext();
      };

      self._twTimer = setTimeout(() => runPhrase(NL_SEARCH_PLACEHOLDERS[idx]), 800);
    },

    _twStop(this: any) {
      if (this._twTimer !== null) {
        clearTimeout(this._twTimer);
        this._twTimer = null;
      }
      this._twPlaceholder = "";
    },

    // ---- Search ------------------------------------------------------------

    /** Alpine class-binding map without inline object literal (prevents Fluid `{...}` clash) */
    btnClassMap(this: any): Record<string, boolean> {
      const q = String(this.query).trim();
      return {
        "ymh-nl-btn--active":    q.length >= 3 && !this.isSubmitted,
        "ymh-nl-btn--submitted": this.isSubmitted && !this.isLoading,
        "ymh-nl-btn--loading":   this.isLoading,
      };
    },

    async submit(this: any) {
      const query = String(this.query).trim();
      if (query.length < 3) return;

      this.isLoading = true;
      this.isSubmitted = true;
      this.hasError = false;

      try {
        const params = new URLSearchParams({
          "tx_vayoga_videoall[action]": "nlSearch",
          "tx_vayoga_videoall[controller]": "Video",
          "tx_vayoga_videoall[query]": query,
          "tx_vayoga_videoall[itemsPerPage]": String(this.itemsPerPage),
          "tx_vayoga_videoall[page]": "0",
          "tx_vayoga_videoall[searchmode]": "ALL",
        });

        const response = await fetch(this.fetchUrl, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params.toString(),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const doc = new DOMParser().parseFromString(await response.text(), "text/html");
        const moreItemsEl = doc.querySelector(".more-items");
        const currentItems = document.querySelector(".tx-va-yoga-video-list-items .items");

        if (moreItemsEl && currentItems) {
          currentItems.innerHTML = moreItemsEl.innerHTML;

          // Store active NL search state globally for the MutationObserver
          window.__ymhNlSearch = { fetchUrl: this.fetchUrl, query };

          // Notify filter bar of NL-interpreted filters. The filter bar's
          // listener will populate its state AND cache the full filter
          // params on the load-more button, so page 2+ carries every
          // concrete filter (no Claude re-interpretation per page).
          const nlFiltersEl = doc.querySelector("[data-nl-filters]");
          const totalCountEl = doc.querySelector("[data-total-count]");
          const nlFiltersJson = nlFiltersEl?.getAttribute("data-nl-filters") ?? "null";
          const totalCount = Number(totalCountEl?.getAttribute("data-total-count") ?? 0);
          if (nlFiltersJson && nlFiltersJson !== "null") {
            document.dispatchEvent(new CustomEvent("ymh:nl-filters", {
              detail: { nlFiltersJson, totalCount, query }
            }));
          }

          // ── Persist snapshot for Back-navigation restore ─────────────────
          // Store query + rendered HTML + filter state in sessionStorage so
          // that when the user navigates to a detail page and presses Back,
          // init() can restore the search result without a new API call.
          try {
            const snapshot: NlSearchSnapshot = {
              query,
              itemsHtml: currentItems.innerHTML,
              nlFiltersJson: nlFiltersJson ?? "null",
              totalCount,
              fetchUrl: this.fetchUrl,
              pageUrl: window.location.pathname + window.location.search,
            };
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(snapshot));
          } catch (_) {
            // sessionStorage full or unavailable — ignore silently
          }

          // Fallback: if for some reason the filter bar doesn't listen
          // (e.g. it's not yet initialised on this page), patch the
          // load-more button with the minimal query-only form. Page 2+
          // will then re-interpret the query on the server — not ideal
          // but better than no pagination at all.
          patchNlLoadMoreButtons();

          // Re-init legacy load-more handler + IntersectionObserver
          window.varioous?.ajax_cl?.initLoadMoreItemsButtons?.();
          window.varioous?.image_cl?.preloadImages?.();
        }

        this.hasResult = true;
      } catch (e) {
        console.error("[naturalSearch] fetch failed:", e);
        this.hasError = true;
        this.isSubmitted = false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Reset the search state (clear text, NL pagination hooks, restored
     * result) — does NOT touch the filter-bar's chips/filters.
     *
     * Dispatches ymh:nl-reset so other components can react: search-feedback
     * resets its own minor UI state, and this component's own root also
     * listens for it (see Search.html) so that clearAll() on the filter bar
     * (the red "Alles löschen" button) can trigger this same reset() to
     * also clear the search text — without the filter bar's clearAll()
     * needing to know anything about this component directly.
     *
     * Guarded against the resulting self-trigger (this function dispatches
     * the very event its own root listens for) with _resettingSelf, so a
     * manual reset (grey X / emptying the field) doesn't run twice and
     * start two overlapping typewriter-placeholder timers.
     */
    reset(this: any) {
      if (this._resettingSelf) return;
      this._resettingSelf = true;

      this.query = "";
      this.hasResult = false;
      this.hasError = false;
      this.isSubmitted = false;
      delete window.__ymhNlSearch;
      document.querySelector(".nl-search-hidden-form")?.remove();
      document.dispatchEvent(new CustomEvent("ymh:nl-reset"));
      // Clear the back-navigation snapshot so a fresh page load shows the
      // default video list, not the last search result.
      try { sessionStorage.removeItem(SESSION_KEY); } catch (_) {}
      this._twStartCycle();

      this._resettingSelf = false;
    },

    /**
     * Attempt to restore the last NL search result from sessionStorage.
     *
     * Returns true (and restores state) when ALL of these conditions hold:
     *   1. A snapshot exists in sessionStorage for the current page URL
     *   2. The browser navigated here via Back/Forward (bfcache or normal back)
     *      — detected via performance.getEntriesByType("navigation")[0].type
     *
     * On success: swaps the DOM, fires ymh:nl-filters to restore filter chips,
     * and sets Alpine state (query, isSubmitted, hasResult) so the UI shows
     * the "submitted" state with the clear button visible.
     *
     * Returns false when no restore is needed (fresh load or no snapshot).
     */
    _restoreFromSession(this: any): boolean {
      // Only restore on back/forward navigation
      const navEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
      const isBackForward = navEntry?.type === "back_forward"
        // Fallback for browsers without Navigation Timing Level 2
        || (performance as any).navigation?.type === 2;

      if (!isBackForward) return false;

      let snapshot: NlSearchSnapshot | null = null;
      try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (!raw) return false;
        snapshot = JSON.parse(raw) as NlSearchSnapshot;
      } catch (_) {
        return false;
      }

      // Guard: only restore if the snapshot was made on this same page
      const currentPage = window.location.pathname + window.location.search;
      if (snapshot.pageUrl !== currentPage) return false;

      // Restore DOM
      const currentItems = document.querySelector(".tx-va-yoga-video-list-items .items");
      if (!currentItems) return false;

      currentItems.innerHTML = snapshot.itemsHtml;

      // Restore Alpine state
      this.query = snapshot.query;
      this.isSubmitted = true;
      this.hasResult = true;
      this.hasError = false;
      this._twStop();

      // Restore global NL state for load-more patching
      window.__ymhNlSearch = { fetchUrl: snapshot.fetchUrl, query: snapshot.query };
      patchNlLoadMoreButtons();

      // Notify filter bar to restore chips
      if (snapshot.nlFiltersJson && snapshot.nlFiltersJson !== "null") {
        // Small delay so Alpine has time to mount the filter bar component
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent("ymh:nl-filters", {
            detail: {
              nlFiltersJson: snapshot!.nlFiltersJson,
              totalCount: snapshot!.totalCount,
              query: snapshot!.query,
            }
          }));
          // Re-init images and load-more
          window.varioous?.ajax_cl?.initLoadMoreItemsButtons?.();
          window.varioous?.image_cl?.preloadImages?.();
        }, 50);
      }

      return true;
    },

    /** Cleanup when Alpine removes the component */
    destroy(this: any) {
      this._twStop();
    },
  };
}
